// Three Good Things (Explore → Relax). A gratitude journal kept on THIS DEVICE.
//
// ⚠️ THE TEXT NEVER LEAVES THE DEVICE. localStorage only — no Supabase, no
// postDraft, no analytics (user's call, 2026-10-27). DSPs will write about
// shifts and the people they support, so entries can carry names and health
// details; keeping them on the phone means DSP Cafe never holds them. The
// screen's copy promises this, and GratitudeScreen.test.jsx asserts it.
//
// Keyed per account, so a second person signing in on the same browser does
// not see someone else's journal. Every storage call is wrapped: private mode
// and blocked site data throw.

export const GRATITUDE_LINES = 3;
export const GRATITUDE_LINE_MAX = 300;
export const GRATITUDE_NOTE_MAX = 1000;
export const GRATITUDE_MAX_ENTRIES = 500;

const keyFor = (userId) => `dspcafe.gratitude.v1.${userId || 'signed-out'}`;

const isEntry = (e) =>
  e && typeof e.id === 'string' && typeof e.createdAt === 'string'
  && Array.isArray(e.lines) && e.lines.every((l) => typeof l === 'string')
  && (e.note === undefined || typeof e.note === 'string');

export function loadEntries(userId) {
  try {
    const raw = localStorage.getItem(keyFor(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isEntry) : [];
  } catch {
    return [];
  }
}

/** Returns false when the browser refused to store (the screen then says so). */
export function saveEntries(userId, entries) {
  try {
    if (entries.length === 0) localStorage.removeItem(keyFor(userId));
    else localStorage.setItem(keyFor(userId), JSON.stringify(entries));
    return true;
  } catch {
    return false;
  }
}

/**
 * Trimmed, capped, non-empty lines plus an optional note ("anything else about
 * your day") → a new entry, or null if nothing was written at all.
 */
export function makeEntry(lines, note = '', now = new Date()) {
  const kept = lines
    .map((l) => (l || '').trim().slice(0, GRATITUDE_LINE_MAX))
    .filter(Boolean)
    .slice(0, GRATITUDE_LINES);
  const keptNote = (note || '').trim().slice(0, GRATITUDE_NOTE_MAX);
  if (kept.length === 0 && !keptNote) return null;
  return {
    id: `${now.getTime()}-${Math.random().toString(36).slice(2, 8)}`,
    createdAt: now.toISOString(),
    lines: kept,
    ...(keptNote ? { note: keptNote } : {}),
  };
}

/** Newest first, oldest dropped past the cap. */
export const addEntry = (entries, entry) => [entry, ...entries].slice(0, GRATITUDE_MAX_ENTRIES);

// Gentle prompts, one per day, the same for everyone on a given date.
export const GRATITUDE_PROMPTS = [
  'Someone you support smiled, laughed or tried something new.',
  'A coworker had your back.',
  'Something you handled well today.',
  'A quiet moment that felt good.',
  'Something small that went right.',
  'Someone who said thank you, or someone you want to thank.',
  'Something you are looking forward to.',
];

export const promptFor = (date = new Date()) => {
  const day = Math.floor(new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime() / 86_400_000);
  return GRATITUDE_PROMPTS[((day % GRATITUDE_PROMPTS.length) + GRATITUDE_PROMPTS.length) % GRATITUDE_PROMPTS.length];
};

// "Today" / "Yesterday" / "Mon, Oct 26" (year added when it isn't this year).
export function dayLabel(date, now = new Date()) {
  const midnight = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const days = Math.round((midnight(now) - midnight(date)) / 86_400_000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  const opts = { weekday: 'short', month: 'short', day: 'numeric' };
  if (date.getFullYear() !== now.getFullYear()) opts.year = 'numeric';
  return date.toLocaleDateString('en-US', opts);
}

/** Entries grouped under their local day, in the order given (newest first). */
export function groupByDay(entries, now = new Date()) {
  const groups = [];
  for (const entry of entries) {
    const label = dayLabel(new Date(entry.createdAt), now);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.entries.push(entry);
    else groups.push({ label, entries: [entry] });
  }
  return groups;
}
